import type { DashboardData, Site, RecurringRevenue, Project, Targets, Campaign, WpSite, NetworkMap } from './types';
import { queryClient } from './queryClient';

export class UnauthorizedError extends Error {
  constructor() {
    super('unauthenticated');
    this.name = 'UnauthorizedError';
  }
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    queryClient.setQueryData(['auth', 'me'], { authenticated: false });
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* ignore */ }
    const msg = (body as { error?: string } | null)?.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

const jsonHeaders = { 'Content-Type': 'application/json' };

export type AuthMe = { authenticated: boolean; user?: { username: string } };

export const api = {
  dashboard: (refresh = false) =>
    fetch(`/api/dashboard${refresh ? '?refresh=1' : ''}`).then(jsonOrThrow<DashboardData>),

  sites: {
    list: async () => {
      const raw = await fetch('/api/sites').then(jsonOrThrow<{ tiers?: unknown; sites: Site[] }>);
      return raw.sites ?? [];
    },
    save: (sites: Site[]) =>
      fetch('/api/sites', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sites }),
      }).then(jsonOrThrow<{ ok: boolean }>),
  },

  targets: () => fetch('/api/targets').then(jsonOrThrow<Targets>),

  recurring: {
    list: async () => {
      const raw = await fetch('/api/recurring').then(jsonOrThrow<{ items: RecurringRevenue[] }>);
      return raw.items ?? [];
    },
    save: (items: RecurringRevenue[]) =>
      fetch('/api/recurring', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      }).then(jsonOrThrow<{ ok: boolean }>),
  },

  projects: {
    list: async () => {
      const raw = await fetch('/api/projects').then(jsonOrThrow<{ projects: Project[] }>);
      return raw.projects ?? [];
    },
    save: (projects: Project[]) =>
      fetch('/api/projects', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projects }),
      }).then(jsonOrThrow<{ ok: boolean }>),
  },

  campaigns: {
    list: async () => {
      const raw = await fetch('/api/campaigns').then(jsonOrThrow<{ campaigns: Campaign[] }>);
      return raw.campaigns ?? [];
    },
    save: (campaigns: Campaign[]) =>
      fetch('/api/campaigns', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaigns }),
      }).then(jsonOrThrow<{ ok: boolean }>),
  },

  maintenance: {
    list: async () => {
      const raw = await fetch('/api/maintenance').then(jsonOrThrow<{ items: WpSite[] }>);
      return raw.items ?? [];
    },
    save: (items: WpSite[]) =>
      fetch('/api/maintenance', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      }).then(jsonOrThrow<{ ok: boolean }>),
  },

  network: () => fetch('/api/network').then(jsonOrThrow<NetworkMap>),

  auth: {
    me: () => fetch('/api/auth/me').then(jsonOrThrow<AuthMe>),
    login: (username: string, password: string) =>
      fetch('/api/auth/login', {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ username, password }),
      }).then(jsonOrThrow<{ ok: boolean; user: { username: string } }>),
    logout: () =>
      fetch('/api/auth/logout', { method: 'POST' }).then(jsonOrThrow<{ ok: boolean }>),
  },

  push: {
    config: () =>
      fetch('/api/push/config').then(jsonOrThrow<{ enabled: boolean; publicKey: string | null }>),
    subscribe: (subscription: PushSubscriptionJSON) =>
      fetch('/api/push/subscribe', {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify(subscription),
      }).then(jsonOrThrow<{ ok: boolean; added: boolean; total: number }>),
    unsubscribe: (endpoint: string) =>
      fetch('/api/push/unsubscribe', {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ endpoint }),
      }).then(jsonOrThrow<{ ok: boolean; removed: number; total: number }>),
    test: () =>
      fetch('/api/push/test', { method: 'POST', headers: jsonHeaders }).then(
        jsonOrThrow<{ ok: boolean; sent: number; failed: number; total: number }>
      ),
  },
};
